import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Avatar,
  IconButton,
  Box,
  useTheme,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import axios from "axios";
import { tokens } from "../../theme";

const CompoundFormDialog = ({
  open,
  onClose,
  compound,
  cmts,
  plans,
  onSuccess,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    logo_url: "",
    status: "active",
    subscription_plan_id: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const fileInputRef = useRef();
  const [isSubmit, setIsSubmit] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    if (compound) {
      // Find current plan in list by name or id for default value
      let selectedPlanId = "";
      if (compound.subscription_plan_name && plans && plans.length > 0) {
        const found = plans.find(
          (p) =>
            p.tier === compound.subscription_plan_name ||
            p.id === compound.subscription_plan_id
        );
        if (found) selectedPlanId = found.id;
      }
      setFormData({
        name: compound.name || "",
        address: compound.address || "",
        logo_url: compound.logo_url || "",
        status: compound.status || "active",
        subscription_plan_id: selectedPlanId || plans?.[0]?.id || "",
      });
      setLogoFile(null);
    } else {
      setFormData({
        name: "",
        address: "",
        logo_url: "",
        status: "active",
        subscription_plan_id: plans?.[0]?.id || "",
      });
      setLogoFile(null);
    }
  }, [compound, plans]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setFormData((prev) => ({
      ...prev,
      logo_url: URL.createObjectURL(file),
    }));
  };

  const handleSubmit = async () => {
    setIsSubmit(true);
    let logoUrl = formData.logo_url;

    // Upload logo if selected
    if (logoFile) {
      try {
        const uploadData = new FormData();
        uploadData.append("file", logoFile);

        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/upload/compound_logo`,
          { method: "POST", body: uploadData }
        );

        const data = await res.json();
        if (res.ok && data) {
          logoUrl = data;
        } else {
          throw new Error("Upload failed");
        }
      } catch (err) {
        setSnackbarMessage("Logo upload failed");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setIsSubmit(false);
        return;
      }
    }

    // Find plan info
    const selectedPlan = plans?.find((p) => p.id === formData.subscription_plan_id);

    // Build payload
    const payload = {
      ...formData,
      logo_url: logoUrl,
      subscription_plan_id: formData.subscription_plan_id,
      subscription_plan_name: selectedPlan?.tier,
      subscription_plan_unitLimit: selectedPlan?.unitLimit,
    };

    try {
      const endpoint = compound
        ? `${process.env.REACT_APP_BACKEND_URL}/api/compound/update/${compound.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/compound/create`;

      const res = await axios.post(endpoint, payload, {
        withCredentials: true,
      });

      if (res.data.ok) {
        setSnackbarMessage(
          compound
            ? "Compound updated successfully!"
            : "Compound created successfully!"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        onSuccess();
        onClose();
      } else {
        throw new Error("Request failed");
      }
    } catch (err) {
      setSnackbarMessage("Submission failed");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }

    setIsSubmit(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.1rem",
            bgcolor: colors.primary[600],
            color: "#fff",
            py: 1.5,
            px: 3,
          }}
        >
          {compound ? "Edit Compound" : "Create Compound"}
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            mt={1}
            mb={3}
            position="relative"
          >
            <Avatar
              src={formData.logo_url}
              sx={{ width: 100, height: 100, border: "2px solid #ccc" }}
            />
            <IconButton
              onClick={() => fileInputRef.current.click()}
              sx={{
                position: "absolute",
                bottom: 0,
                right: "calc(50% - 50px)",
                backgroundColor: "#B0B0B0",
                boxShadow: 1,
                zIndex: 1,
              }}
            >
              <UploadIcon />
            </IconButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileSelect}
            />
          </Box>

          <Box mb={2}>
            <TextField
              name="name"
              label="Compound Name"
              fullWidth
              value={formData.name}
              onChange={handleChange}
            />
          </Box>
          <Box mb={2}>
            <TextField
              name="address"
              label="Address"
              fullWidth
              value={formData.address}
              onChange={handleChange}
            />
          </Box>
          <Box mb={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box mb={2}>
            <FormControl fullWidth>
              <InputLabel>Subscription Plan</InputLabel>
              <Select
                name="subscription_plan_id"
                value={formData.subscription_plan_id}
                onChange={handleChange}
                label="Subscription Plan"
              >
                {plans &&
                  plans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.tier} ({plan.unitLimit} Units)
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmit}
          >
            {isSubmit
              ? compound
                ? "Updating ..."
                : "Creating ..."
              : compound
              ? "Update"
              : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompoundFormDialog;
